import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiTrash2, FiUploadCloud } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { postsAPI } from '../../services/api';
import { API_URL, CATEGORIES, STORAGE_KEYS } from '../../utils/constants';
import { resolveMediaUrl } from '../../utils/media';
import '../Create/Create.css';
import './EditPost.css';

const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const safePostSlug = (post) => {
  const base = slugify(post?.title) || 'post';
  if (post?.slug) return post.slug;
  if (post?.id) return `${base}-${post.id}`;
  return base;
};

const uploadMediaFile = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/media/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${localStorage.getItem(STORAGE_KEYS.TOKEN)}`
    },
    body: fd
  });
  if (!res.ok) throw new Error('Upload failed');
  const data = await res.json();
  return data?.media?.file_path || null;
};

const normalizeSectionImages = (images) => {
  if (!Array.isArray(images)) return [];
  return images
    .filter(Boolean)
    .map((img, idx) => ({
      id: img.id,
      localId: String(img.id || `img-${idx}-${Date.now()}`),
      image_url: img.image_url || img.imageUrl || img.url || '',
      caption: img.caption || '',
      alt_text: img.alt_text || img.altText || '',
      position: img.position ?? (idx + 1)
    }))
    .filter((img) => Boolean(img.image_url));
};

const sectionLabelFromType = (t) => {
  switch (t) {
    case 'abstract':
      return 'Resumen';
    case 'intro':
      return 'Introducción';
    case 'section':
      return 'Sección';
    case 'subsection':
      return 'Subsección';
    case 'conclusion':
      return 'Conclusiones';
    case 'bibliography':
      return 'Referencias';
    default:
      return t;
  }
};

export default function EditPost() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const submitLockRef = useRef(false);

  const [post, setPost] = useState(null);

  // Shared fields
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');

  // Media (optional replace)
  const [newImageFile, setNewImageFile] = useState(null);
  const [newVideoFile, setNewVideoFile] = useState(null);
  const [newThumbnailFile, setNewThumbnailFile] = useState(null);

  // Article sections
  const [sections, setSections] = useState([]);

  const [sectionUploadingId, setSectionUploadingId] = useState(null);

  const isArticle = post?.type === 'article';
  const isMeme = post?.type === 'meme';
  const isVideo = post?.type === 'video-horizontal' || post?.type === 'video-reel';

  const categoryOptions = useMemo(() => CATEGORIES || [], []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await postsAPI.getById(id);
        const p = res.data;
        setPost(p);
        setTitle(p?.title || '');
        setCategory(p?.category || '');
        setDescription(p?.description || p?.content || '');

        if (p?.type === 'article') {
          if (Array.isArray(p.sections) && p.sections.length > 0) {
            setSections(
              p.sections
                .slice()
                .sort((a, b) => (a.position || 0) - (b.position || 0))
                .map((s, idx) => ({
                  id: s.id,
                  localId: String(s.id || `section-${idx + 1}`),
                  section_type: s.section_type,
                  title: s.title || sectionLabelFromType(s.section_type),
                  content: s.content || '',
                  position: s.position || idx + 1,
                  images: normalizeSectionImages(s.images)
                }))
            );
          } else {
            const fallback = [
              { section_type: 'abstract', title: 'Resumen', content: p.abstract || '', position: 1 },
              { section_type: 'intro', title: 'Introducción', content: p.introduction || '', position: 2 },
              { section_type: 'section', title: 'Marco Teórico', content: p.theoretical_framework || '', position: 3 },
              { section_type: 'section', title: 'Metodología', content: p.methodology || '', position: 4 },
              { section_type: 'section', title: 'Resultados', content: p.results || '', position: 5 },
              { section_type: 'section', title: 'Discusión', content: p.discussion || '', position: 6 },
              { section_type: 'conclusion', title: 'Conclusiones', content: p.conclusions || '', position: 7 }
            ]
              .filter((s) => s.content)
              .map((s, idx) => ({ ...s, localId: String(`section-${idx + 1}`), images: [] }));
            setSections(fallback);
          }
        }
      } catch (error) {
        console.error('Error loading post:', error);
        toast.error('No se pudo cargar la publicación');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleUpdateSectionContent = (localId, value) => {
    setSections((prev) => prev.map((s) => (s.localId === localId ? { ...s, content: value } : s)));
  };

  const handleAddSectionImage = async (sectionLocalId, file) => {
    if (!file) return;
    setSectionUploadingId(sectionLocalId);
    try {
      const url = await uploadMediaFile(file);
      if (!url) throw new Error('No url');
      setSections((prev) => prev.map((s) => {
        if (s.localId !== sectionLocalId) return s;
        const nextImages = Array.isArray(s.images) ? [...s.images] : [];
        nextImages.push({
          localId: `new-${Date.now()}`,
          image_url: url,
          caption: '',
          alt_text: '',
          position: nextImages.length + 1
        });
        return { ...s, images: nextImages };
      }));
      toast.success('Imagen agregada');
    } catch (e) {
      console.error('Section image upload error:', e);
      toast.error('No se pudo subir la imagen');
    } finally {
      setSectionUploadingId(null);
    }
  };

  const handleRemoveSectionImage = (sectionLocalId, imageLocalId) => {
    setSections((prev) => prev.map((s) => {
      if (s.localId !== sectionLocalId) return s;
      const nextImages = (s.images || []).filter((img) => (img.localId || String(img.id)) !== imageLocalId);
      return {
        ...s,
        images: nextImages.map((img, idx) => ({ ...img, position: idx + 1 }))
      };
    }));
  };

  const handleUpdateSectionImageField = (sectionLocalId, imageLocalId, field, value) => {
    setSections((prev) => prev.map((s) => {
      if (s.localId !== sectionLocalId) return s;
      const nextImages = (s.images || []).map((img) => {
        const key = img.localId || String(img.id);
        if (key !== imageLocalId) return img;
        return { ...img, [field]: value };
      });
      return { ...s, images: nextImages };
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current || saving) return;
    submitLockRef.current = true;
    setSaving(true);

    try {
      let uploadedImagePath = null;
      let uploadedVideoPath = null;
      let uploadedThumbPath = null;

      if (newImageFile) {
        uploadedImagePath = await uploadMediaFile(newImageFile);
      }
      if (newVideoFile) {
        uploadedVideoPath = await uploadMediaFile(newVideoFile);
      }
      if (newThumbnailFile) {
        uploadedThumbPath = await uploadMediaFile(newThumbnailFile);
      }

      const payload = {
        title,
        category
      };

      if (!isArticle) {
        payload.description = description;
        payload.content = description;
        payload.excerpt = (description || '').slice(0, 200);
      } else {
        const desc = (description || '').trim();
        if (desc) {
          payload.description = desc;
          payload.excerpt = desc.slice(0, 200);
        }
      }

      if (uploadedImagePath) {
        payload.image = uploadedImagePath;
      }

      if (uploadedThumbPath) {
        payload.image = uploadedThumbPath;
      }

      if (isVideo && uploadedVideoPath) {
        payload.video_url = uploadedVideoPath;
      }

      if (isArticle) {
        payload.sections = sections.map((s, idx) => ({
          id: s.id,
          section_type: s.section_type,
          title: s.title,
          content: s.content,
          position: s.position || idx + 1,
          images: (s.images || []).map((img, imgIdx) => ({
            id: img.id,
            image_url: img.image_url,
            caption: img.caption || null,
            alt_text: img.alt_text || null,
            position: img.position || (imgIdx + 1)
          }))
        }));

        // Best-effort legacy fields for compatibility
        const pick = (t) => sections.find((s) => s.section_type === t)?.content || '';
        payload.abstract = pick('abstract');
        payload.introduction = pick('intro');
        payload.theoretical_framework = sections.find((s) => s.title === 'Marco Teórico')?.content || '';
        payload.methodology = sections.find((s) => s.title === 'Metodología')?.content || '';
        payload.results = sections.find((s) => s.title === 'Resultados')?.content || '';
        payload.discussion = sections.find((s) => s.title === 'Discusión')?.content || '';
        payload.conclusions = sections.find((s) => s.section_type === 'conclusion')?.content || '';
      }

      const resp = await postsAPI.update(post.id, payload);
      toast.success('Publicación actualizada');

      const updated = resp?.data?.post || post;
      if (updated?.type === 'article') navigate(`/article/${safePostSlug(updated)}`);
      else if (updated?.type === 'meme') navigate(`/meme/${safePostSlug(updated)}`);
      else if (updated?.type === 'video-horizontal' || updated?.type === 'video-reel') navigate(`/video/${safePostSlug(updated)}`);
      else navigate(-1);
    } catch (error) {
      console.error('Error updating post:', error);
      toast.error('No se pudo guardar');
    } finally {
      submitLockRef.current = false;
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="create-page">
        <div className="create-container">
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="create-page">
        <div className="create-container">
          <p>Publicación no encontrada</p>
          <Link to="/">Volver</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="create-page">
      <div className="create-container">
        <header className="create-header">
          <div className="editpost-header-row">
            <button type="button" className="back-button" onClick={() => navigate(-1)}>
              <FiArrowLeft />
              <span>Volver</span>
            </button>
            <h1>Editar publicación</h1>
          </div>
          <p>{post.type === 'article' ? 'Artículo' : post.type === 'meme' ? 'Meme' : 'Video'}</p>
        </header>

        <form className="simple-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Título *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>

          <div className="form-group">
            <label>Categoría *</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} required>
              <option value="">Selecciona una categoría</option>
              {categoryOptions.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          </div>

          {(isVideo || isMeme) && (
            <div className="form-group">
              <label>Descripción</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
            </div>
          )}

          {isArticle && (
            <div className="form-group">
              <label>Descripción (opcional)</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
            </div>
          )}

          {(isMeme || isArticle) && (
            <div className="form-group">
              <label>{isMeme ? 'Imagen del meme (opcional)' : 'Portada (opcional)'}</label>
              {post.image && (
                <img
                  src={resolveMediaUrl(post.image)}
                  alt="preview"
                  style={{ maxWidth: '100%', borderRadius: 12, marginBottom: 10 }}
                />
              )}
              <input type="file" accept="image/*" onChange={(e) => setNewImageFile(e.target.files?.[0] || null)} />
            </div>
          )}

          {isVideo && (
            <div className="form-group">
              <label>Video (opcional)</label>
              <p style={{ marginTop: 0 }}>
                Actual: <code>{post.video_url || '(sin video)'}</code>
              </p>
              <input type="file" accept="video/*" onChange={(e) => setNewVideoFile(e.target.files?.[0] || null)} />
              <p className="help-text">
                <FiUploadCloud style={{ marginRight: 6 }} /> Subir un nuevo archivo reemplaza el video.
              </p>

              <label style={{ marginTop: 12 }}>Miniatura (opcional)</label>
              <input type="file" accept="image/*" onChange={(e) => setNewThumbnailFile(e.target.files?.[0] || null)} />
            </div>
          )}

          {isArticle && sections.length > 0 && (
            <div className="form-group">
              <label>Secciones</label>
              <div className="editpost-sections">
                {sections.map((s) => (
                  <div key={s.localId || s.id || `${s.section_type}-${s.title}-${s.position}`} className="editpost-section">
                    <div className="editpost-section-title">{s.title || sectionLabelFromType(s.section_type)}</div>
                    <textarea
                      value={s.content || ''}
                      onChange={(e) => handleUpdateSectionContent(s.localId, e.target.value)}
                      rows={6}
                    />

                    <div className="editpost-section-images">
                      <div className="editpost-section-images-header">
                        <div className="editpost-section-images-title">Imágenes</div>
                        <label className="editpost-upload-btn">
                          <FiUploadCloud style={{ marginRight: 6 }} />
                          {sectionUploadingId === s.localId ? 'Subiendo...' : 'Agregar imagen'}
                          <input
                            type="file"
                            accept="image/*"
                            disabled={sectionUploadingId === s.localId}
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              e.target.value = '';
                              handleAddSectionImage(s.localId, file);
                            }}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>

                      {Array.isArray(s.images) && s.images.length > 0 ? (
                        <div className="editpost-images-grid">
                          {s.images.map((img) => {
                            const imgKey = img.localId || String(img.id);
                            return (
                              <div key={imgKey} className="editpost-image-card">
                                <img
                                  className="editpost-image-thumb"
                                  src={resolveMediaUrl(img.image_url)}
                                  alt={img.alt_text || s.title}
                                />
                                <div className="editpost-image-fields">
                                  <input
                                    type="text"
                                    placeholder="Caption (opcional)"
                                    value={img.caption || ''}
                                    onChange={(e) => handleUpdateSectionImageField(s.localId, imgKey, 'caption', e.target.value)}
                                  />
                                  <input
                                    type="text"
                                    placeholder="Alt text (opcional)"
                                    value={img.alt_text || ''}
                                    onChange={(e) => handleUpdateSectionImageField(s.localId, imgKey, 'alt_text', e.target.value)}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-secondary editpost-remove-image"
                                    onClick={() => handleRemoveSectionImage(s.localId, imgKey)}
                                  >
                                    <FiTrash2 style={{ marginRight: 6 }} /> Quitar
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="editpost-images-empty">Sin imágenes en esta sección.</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={saving}>
            <FiSave style={{ marginRight: 8 }} />
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </form>
      </div>
    </div>
  );
}
